import { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { Brain, Plus, CheckCircle, Lock, ChevronDown, ChevronUp, Search, X, MapPin, ExternalLink, MessageSquare, Download, FileText } from 'lucide-react'
import api from '../../services/api'
import { useAuthStore } from '../../stores/authStore'

const SEV_COLOUR: Record<string, string> = {
  critical: '#DC2626', high: '#D97706', medium: '#2563EB', low: '#16A34A',
}
const CAT_LABEL: Record<string, string> = {
  injury: 'Injury', near_miss: 'Near Miss', property_damage: 'Property Damage',
  environmental: 'Environmental', hazard: 'Hazard', illness: 'Illness',
}

const STATUS_COLOURS: Record<string, string> = {
  open: 'var(--orange)', in_progress: 'var(--amber)', findings_review: 'var(--accent)', closed: 'var(--green)',
}
const EVIDENCE_TYPES = ['physical', 'documentary', 'photographic', 'digital', 'testimonial', 'cctv']
const STATUS_STEPS = ['open', 'in_progress', 'findings_review', 'closed'] as const

const COMM_METHODS = [
  { value: 'toolbox_talk', label: 'Toolbox talk' },
  { value: 'safety_alert', label: 'Safety alert (written)' },
  { value: 'training_update', label: 'Training update' },
  { value: 'team_meeting', label: 'Team meeting' },
  { value: 'other', label: 'Other' },
]

export default function InvestigationDetail() {
  const { id } = useParams<{ id: string }>()
  const { user, accessToken } = useAuthStore()
  const [inv, setInv] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  // Edit state
  const [editing, setEditing] = useState<string | null>(null)
  const [editText, setEditText] = useState('')
  const [savingEdit, setSavingEdit] = useState(false)

  // Evidence form
  const [showEvidence, setShowEvidence] = useState(false)
  const [evForm, setEvForm] = useState({ item_description: '', evidence_type: 'documentary', collection_location: '', relevance: '' })
  const [savingEv, setSavingEv] = useState(false)

  // Close form
  const [showClose, setShowClose] = useState(false)
  const [closeForm, setCloseForm] = useState({ summary: '', findings: '', recommendations: '' })
  const [closing, setClosing] = useState(false)
  const [evidenceWarnAcknowledged, setEvidenceWarnAcknowledged] = useState(false)

  // Communication to workers
  const [editingComm, setEditingComm] = useState(false)
  const [commForm, setCommForm] = useState({ method: '', date: '', summary: '', by: '' })
  const [savingComm, setSavingComm] = useState(false)

  // AI analysis
  const [analysing, setAnalysing] = useState(false)
  const [aiOpen, setAiOpen] = useState(false)

  // Report download
  const [reportDownloading, setReportDownloading] = useState<string | null>(null)
  const [showReportMenu, setShowReportMenu] = useState(false)

  const downloadReport = async (format: 'pdf' | 'docx') => {
    if (!inv) return
    setReportDownloading(format)
    setShowReportMenu(false)
    try {
      const resp = await fetch(`/api/investigations/${id}/report.${format}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      })
      if (!resp.ok) throw new Error()
      const blob = await resp.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `${inv.investigation_number}-report.${format}`
      a.click()
      URL.revokeObjectURL(url)
    } catch { alert('Report generation failed. Try again.') }
    setReportDownloading(null)
  }

  // Incident linking
  const [linkingIncident, setLinkingIncident] = useState(false)
  const [incSearch, setIncSearch] = useState('')
  const [incResults, setIncResults] = useState<any[]>([])
  const [incPickerOpen, setIncPickerOpen] = useState(false)
  const [incLinkLoading, setIncLinkLoading] = useState(false)

  const load = async () => {
    try {
      const { data } = await api.get(`/investigations/${id}`)
      setInv(data)
      setCommForm({
        method: data.worker_comm_method || '',
        date: data.worker_comm_date || '',
        summary: data.worker_comm_summary || '',
        by: data.worker_comm_by || '',
      })
    } catch { setError('Failed to load investigation') }
    setLoading(false)
  }

  useEffect(() => { load() }, [id])

  const startEdit = (field: string, current: string) => {
    setEditing(field)
    setEditText(current || '')
  }

  const saveField = async () => {
    if (!editing) return
    setSavingEdit(true)
    try {
      const { data } = await api.patch(`/investigations/${id}`, { [editing]: editText })
      setInv((prev: any) => ({ ...prev, ...data }))
      setEditing(null)
    } catch {}
    setSavingEdit(false)
  }

  const runAI = async () => {
    setAnalysing(true)
    setError('')
    try {
      const { data } = await api.post(`/investigations/${id}/ai-analysis`, {})
      setInv((prev: any) => ({
        ...prev,
        icam_analysis: data.analysis,
        immediate_causes: data.immediate_causes || prev.immediate_causes,
        contributing_factors: data.contributing_factors || prev.contributing_factors,
        root_causes: data.root_causes || prev.root_causes,
        recommendations: data.recommendations || prev.recommendations,
      }))
      setAiOpen(true)
    } catch (err: any) {
      setError(err.response?.data?.message || 'AI analysis failed')
    }
    setAnalysing(false)
  }

  const addEvidence = async () => {
    if (!evForm.item_description.trim()) return
    setSavingEv(true)
    try {
      await api.post(`/investigations/${id}/evidence`, evForm)
      await load()
      setEvForm({ item_description: '', evidence_type: 'documentary', collection_location: '', relevance: '' })
      setShowEvidence(false)
    } catch {}
    setSavingEv(false)
  }

  const saveComm = async () => {
    setSavingComm(true)
    try {
      const { data } = await api.patch(`/investigations/${id}`, {
        worker_comm_method: commForm.method || null,
        worker_comm_date: commForm.date || null,
        worker_comm_summary: commForm.summary || null,
        worker_comm_by: commForm.by || null,
      })
      setInv((prev: any) => ({ ...prev, ...data }))
      setEditingComm(false)
    } catch {}
    setSavingComm(false)
  }

  const closeInvestigation = async () => {
    if (!closeForm.summary.trim()) return

    if (!inv.worker_comm_method) {
      setError('Complete the "Communication to Workers" section below before closing.')
      setShowClose(false)
      setEditingComm(true)
      return
    }

    const isHighSev = inv.ai_severity === 'high' || inv.ai_severity === 'critical'
    if (isHighSev && (inv.evidence?.length || 0) === 0 && !evidenceWarnAcknowledged) {
      setEvidenceWarnAcknowledged(true)
      return
    }

    setClosing(true)
    setError('')
    try {
      const { data } = await api.post(`/investigations/${id}/close`, closeForm)
      setInv((prev: any) => ({ ...prev, ...data }))
      setShowClose(false)
      setEvidenceWarnAcknowledged(false)
    } catch (err: any) {
      setError(err.response?.data?.message || err.response?.data?.error || 'Close failed')
    }
    setClosing(false)
  }

  const updateStatus = async (status: string) => {
    try {
      const { data } = await api.patch(`/investigations/${id}`, { status })
      setInv((prev: any) => ({ ...prev, ...data }))
    } catch {}
  }

  const linkIncident = async (incidentId: string) => {
    try {
      await api.patch(`/investigations/${id}`, { incident_id: incidentId })
      await load()
      setLinkingIncident(false)
      setIncSearch('')
      setIncPickerOpen(false)
    } catch {}
  }

  useEffect(() => {
    if (!linkingIncident) return
    setIncLinkLoading(true)
    api.get('/investigations/incidents-search', { params: incSearch ? { q: incSearch } : {} })
      .then(r => setIncResults(r.data || []))
      .catch(() => {})
      .finally(() => setIncLinkLoading(false))
  }, [linkingIncident, incSearch])

  if (loading) return <div className="text-center py-16" style={{ color: 'var(--text-muted)' }}>Loading...</div>
  if (!inv) return <div className="text-center py-16" style={{ color: 'var(--red)' }}>{error || 'Not found'}</div>

  const isClosed = inv.status === 'closed'
  const allCAsClosed = inv.corrective_actions?.length > 0 && inv.corrective_actions.every((ca: any) => ca.status === 'closed')
  const showReadyToClose = inv.status === 'findings_review' && allCAsClosed

  const isHighSev = inv.ai_severity === 'high' || inv.ai_severity === 'critical'
  const showEvidenceWarn = evidenceWarnAcknowledged && isHighSev && (inv.evidence?.length || 0) === 0

  const commMethodLabel = COMM_METHODS.find(m => m.value === inv.worker_comm_method)?.label || inv.worker_comm_method

  return (
    <div className="max-w-4xl mx-auto space-y-6">

      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <Link to="/investigations" className="text-sm" style={{ color: 'var(--accent)' }}>← Investigations</Link>
          <h1 className="text-2xl font-bold mt-1" style={{ color: 'var(--text)' }}>{inv.investigation_number}</h1>
          <p className="text-sm mt-1" style={{ color: 'var(--text-muted)' }}>{inv.title}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap justify-end">
          <span
            className="badge capitalize"
            style={{ background: STATUS_COLOURS[inv.status] + '20', color: STATUS_COLOURS[inv.status] }}
          >
            {inv.status?.replace('_', ' ')}
          </span>
          {/* Download Report dropdown */}
          <div style={{ position: 'relative' }}>
            <button
              onClick={() => setShowReportMenu(m => !m)}
              disabled={!!reportDownloading}
              className="btn btn-secondary text-sm flex items-center gap-1.5"
            >
              <Download size={13} />
              {reportDownloading ? `Generating ${reportDownloading.toUpperCase()}…` : 'Download Report'}
            </button>
            {showReportMenu && (
              <div
                style={{
                  position: 'absolute', top: '100%', right: 0, marginTop: 4,
                  background: 'var(--surface)', border: '1px solid var(--border)',
                  borderRadius: 10, boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
                  minWidth: 160, zIndex: 50,
                }}
                onMouseLeave={() => setShowReportMenu(false)}
              >
                <button
                  onClick={() => downloadReport('pdf')}
                  style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: 'var(--text)' }}
                  onMouseEnter={e => (e.currentTarget.style.background = 'var(--bg)')}
                  onMouseLeave={e => (e.currentTarget.style.background = 'none')}
                >
                  <FileText size={14} style={{ color: 'var(--red)' }} /> PDF Report
                </button>
                <div style={{ height: 1, background: 'var(--border)', margin: '0 8px' }} />
                <button
                  onClick={() => downloadReport('docx')}
                  style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: 'var(--text)' }}
                  onMouseEnter={e => (e.currentTarget.style.background = 'var(--bg)')}
                  onMouseLeave={e => (e.currentTarget.style.background = 'none')}
                >
                  <FileText size={14} style={{ color: 'var(--blue)' }} /> Word Document
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Linked Incident card */}
      <div className="card">
        {!linkingIncident && inv.incident_id ? (
          <div className="space-y-3">
            <div className="flex items-center justify-between gap-4">
              <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>Linked Incident</p>
              {!isClosed && (
                <button
                  onClick={() => setLinkingIncident(true)}
                  style={{ fontSize: 12, color: 'var(--accent)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                >
                  Change
                </button>
              )}
            </div>

            <div style={{
              borderRadius: 10, padding: '12px 14px',
              background: 'var(--accent-light)', border: '1px solid var(--accent)',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 6 }}>
                <Link to={`/incidents/${inv.incident_id}`}
                  style={{ fontWeight: 700, fontSize: 14, color: 'var(--accent)', display: 'flex', alignItems: 'center', gap: 4 }}>
                  {inv.incident_number} <ExternalLink size={12} />
                </Link>
                {inv.ai_severity && (
                  <span style={{
                    fontSize: 10, padding: '1px 6px', borderRadius: 4, fontWeight: 700,
                    background: SEV_COLOUR[inv.ai_severity] + '20', color: SEV_COLOUR[inv.ai_severity],
                  }}>{inv.ai_severity}</span>
                )}
                {inv.category && (
                  <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{CAT_LABEL[inv.category] || inv.category}</span>
                )}
                {inv.date_occurred && (
                  <span style={{ fontSize: 11, color: 'var(--text-muted)', marginLeft: 'auto' }}>
                    {new Date(inv.date_occurred).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}
                  </span>
                )}
              </div>

              {inv.incident_description && (
                <p style={{ fontSize: 13, color: 'var(--text)', lineHeight: 1.6, margin: '0 0 8px' }}>
                  {inv.incident_description}
                </p>
              )}

              <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 12, color: 'var(--text-muted)' }}>
                {inv.site_name && (
                  <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                    <MapPin size={11} /> {inv.site_name}
                  </span>
                )}
                {inv.injury_type && <span>Injury: <strong style={{ color: 'var(--text)' }}>{inv.injury_type}</strong></span>}
                {inv.activity_at_time && <span>Activity: <strong style={{ color: 'var(--text)' }}>{inv.activity_at_time}</strong></span>}
                {inv.location_detail && <span>Location: <strong style={{ color: 'var(--text)' }}>{inv.location_detail}</strong></span>}
              </div>
            </div>
          </div>
        ) : linkingIncident ? (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-sm font-semibold" style={{ color: 'var(--text)' }}>
                {inv.incident_id ? 'Change Linked Incident' : 'Link Incident'}
              </p>
              <button onClick={() => { setLinkingIncident(false); setIncSearch(''); setIncPickerOpen(false) }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
                <X size={14} style={{ color: 'var(--text-muted)' }} />
              </button>
            </div>

            <div style={{ position: 'relative' }}>
              <Search size={14} style={{
                position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)',
                color: 'var(--text-muted)', pointerEvents: 'none',
              }} />
              <input
                style={{ paddingLeft: 32 }}
                placeholder="Search incidents…"
                value={incSearch}
                autoFocus
                onFocus={() => setIncPickerOpen(true)}
                onChange={e => { setIncSearch(e.target.value); setIncPickerOpen(true) }}
                onBlur={() => setTimeout(() => setIncPickerOpen(false), 160)}
              />
            </div>

            {incPickerOpen && (
              <div style={{
                borderRadius: 10, border: '1px solid var(--border)',
                boxShadow: '0 4px 16px rgba(0,0,0,0.1)', maxHeight: 260, overflowY: 'auto',
              }}>
                {incLinkLoading ? (
                  <div style={{ padding: '12px 16px', fontSize: 13, color: 'var(--text-muted)' }}>Loading…</div>
                ) : incResults.length === 0 ? (
                  <div style={{ padding: '12px 16px', fontSize: 13, color: 'var(--text-muted)' }}>No incidents found</div>
                ) : incResults.map((inc, i) => (
                  <div
                    key={inc.id}
                    onMouseDown={() => linkIncident(inc.id)}
                    style={{
                      padding: '10px 14px', cursor: 'pointer',
                      borderBottom: i < incResults.length - 1 ? '1px solid var(--border)' : 'none',
                      background: 'transparent', transition: 'background 0.1s',
                    }}
                    onMouseEnter={e => (e.currentTarget.style.background = 'var(--bg)')}
                    onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 2 }}>
                      <span style={{ fontWeight: 700, fontSize: 13, color: 'var(--accent)' }}>{inc.incident_number}</span>
                      {inc.ai_severity && (
                        <span style={{
                          fontSize: 10, padding: '1px 5px', borderRadius: 4, fontWeight: 600,
                          background: SEV_COLOUR[inc.ai_severity] + '20', color: SEV_COLOUR[inc.ai_severity],
                        }}>{inc.ai_severity}</span>
                      )}
                      <span style={{ fontSize: 11, color: 'var(--text-muted)', marginLeft: 'auto' }}>
                        {new Date(inc.date_occurred).toLocaleDateString('en-AU')}
                      </span>
                    </div>
                    <p style={{ fontSize: 12, color: 'var(--text)', margin: 0 }}>{(inc.description || '').slice(0, 100)}</p>
                  </div>
                ))}
              </div>
            )}

            <button onClick={() => { setLinkingIncident(false); setIncSearch('') }} className="btn btn-secondary text-sm">Cancel</button>
          </div>
        ) : (
          <div className="flex items-center justify-between">
            <p className="text-sm" style={{ color: 'var(--text-muted)' }}>No incident linked</p>
            {!isClosed && (
              <button onClick={() => setLinkingIncident(true)} className="btn btn-secondary text-sm flex items-center gap-1.5">
                <Search size={13} /> Link Incident
              </button>
            )}
          </div>
        )}
      </div>

      {/* Status progression */}
      <div className="card space-y-4">
        <div className="flex items-center gap-2 flex-wrap">
          {STATUS_STEPS.map((s, i, arr) => {
            const isActive = inv.status === s
            const isPast = arr.indexOf(inv.status) > i
            const colour = STATUS_COLOURS[s]
            return (
              <div key={s} className="flex items-center gap-2">
                <div
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold cursor-pointer select-none"
                  style={{
                    background: isActive || isPast ? colour + '20' : 'var(--bg)',
                    color: isActive || isPast ? colour : 'var(--text-muted)',
                    border: `1px solid ${isActive || isPast ? colour : 'var(--border)'}`,
                  }}
                  onClick={() => !isClosed && s !== 'closed' && updateStatus(s)}
                >
                  {isPast && <CheckCircle size={12} />}
                  {isActive && <span className="w-1.5 h-1.5 rounded-full inline-block" style={{ background: colour }} />}
                  {s.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                </div>
                {i < arr.length - 1 && <span style={{ color: 'var(--border)' }}>›</span>}
              </div>
            )
          })}
        </div>

        <div className="grid grid-cols-3 gap-4 text-sm">
          <div><span style={{ color: 'var(--text-muted)' }}>Lead: </span><strong style={{ color: 'var(--text)' }}>{inv.lead_name || '—'}</strong></div>
          <div><span style={{ color: 'var(--text-muted)' }}>Method: </span><strong style={{ color: 'var(--text)' }}>{inv.methodology?.toUpperCase()}</strong></div>
          <div><span style={{ color: 'var(--text-muted)' }}>Target: </span>
            <strong style={{ color: inv.target_completion && new Date(inv.target_completion) < new Date() && !isClosed ? 'var(--red)' : 'var(--text)' }}>
              {inv.target_completion ? new Date(inv.target_completion).toLocaleDateString('en-AU') : '—'}
            </strong>
          </div>
        </div>

        {/* Ready-to-close prompt */}
        {showReadyToClose && (
          <div className="flex items-center justify-between p-3 rounded-lg" style={{ background: 'var(--green-light)', border: '1px solid var(--green)' }}>
            <div className="flex items-center gap-2">
              <CheckCircle size={16} style={{ color: 'var(--green)', flexShrink: 0 }} />
              <p className="text-sm font-medium" style={{ color: 'var(--green)' }}>
                All corrective actions closed — ready to finalise this investigation.
              </p>
            </div>
            {!showClose && (
              <button onClick={() => setShowClose(true)} className="btn text-xs px-3 py-1.5"
                style={{ background: 'var(--green)', color: '#fff', border: 'none', flexShrink: 0 }}>
                Close now
              </button>
            )}
          </div>
        )}

        {/* Error message */}
        {error && (
          <div className="text-sm p-3 rounded-lg" style={{ background: 'var(--red-light)', color: 'var(--red)' }}>{error}</div>
        )}

        {!isClosed && (
          <div className="flex gap-3">
            {!showClose && (
              <button onClick={() => { setShowClose(true); setError('') }} className="btn flex items-center gap-2"
                style={{ background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--text)' }}>
                <Lock size={14} /> Close Investigation
              </button>
            )}
          </div>
        )}

        {showClose && (
          <div className="space-y-3 pt-3" style={{ borderTop: '1px solid var(--border)' }}>
            <p className="text-sm font-semibold" style={{ color: 'var(--text)' }}>Close Investigation</p>

            {/* Evidence warning */}
            {showEvidenceWarn && (
              <div className="p-3 rounded-lg" style={{ background: '#FEF3C7', border: '1px solid #D97706' }}>
                <p className="text-sm font-semibold" style={{ color: '#92400E' }}>No evidence on record</p>
                <p className="text-xs mt-1" style={{ color: '#92400E' }}>
                  This is a {inv.ai_severity}-severity incident with no evidence items logged. Consider adding photos, witness statements, or physical evidence before closing.
                </p>
                <div className="flex gap-2 mt-2">
                  <button onClick={() => { setShowClose(false); setShowEvidence(true); setEvidenceWarnAcknowledged(false) }}
                    className="btn btn-secondary text-xs py-1">Add evidence first</button>
                  <button onClick={closeInvestigation} className="btn text-xs py-1" style={{ background: '#D97706', color: '#fff', border: 'none' }}>
                    Close anyway
                  </button>
                </div>
              </div>
            )}

            {!showEvidenceWarn && (
              <>
                <div>
                  <label className="text-sm font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>Executive Summary *</label>
                  <textarea rows={3} placeholder="Brief summary of investigation outcomes..." value={closeForm.summary} onChange={e => setCloseForm(f => ({ ...f, summary: e.target.value }))} autoFocus />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>Key Findings</label>
                    <textarea rows={3} placeholder="List of key findings..." value={closeForm.findings} onChange={e => setCloseForm(f => ({ ...f, findings: e.target.value }))} />
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>Recommendations</label>
                    <textarea rows={3} placeholder="Actions recommended..." value={closeForm.recommendations} onChange={e => setCloseForm(f => ({ ...f, recommendations: e.target.value }))} />
                  </div>
                </div>
                <div className="flex gap-3">
                  <button onClick={closeInvestigation} disabled={closing || !closeForm.summary.trim()} className="btn btn-danger flex items-center gap-2">
                    <Lock size={14} />{closing ? 'Closing...' : 'Confirm Close'}
                  </button>
                  <button onClick={() => { setShowClose(false); setEvidenceWarnAcknowledged(false) }} className="btn btn-secondary">Cancel</button>
                </div>
              </>
            )}
          </div>
        )}

        {isClosed && inv.summary && (
          <div className="p-4 rounded-xl space-y-1" style={{ background: 'var(--bg)', border: '1px solid var(--border)' }}>
            <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>Investigation Summary</p>
            <p className="text-sm whitespace-pre-wrap" style={{ color: 'var(--text)', lineHeight: 1.7 }}>{inv.summary}</p>
          </div>
        )}
      </div>

      {/* ICAM Analysis sections */}
      <div className="card space-y-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Brain size={20} style={{ color: 'var(--purple)' }} />
            <h2 className="font-semibold" style={{ color: 'var(--text)' }}>ICAM Analysis</h2>
          </div>
          {!isClosed && (
            <button onClick={runAI} disabled={analysing} className="btn btn-secondary text-sm">
              {analysing ? 'Analysing...' : inv.icam_analysis ? 'Re-run AI' : 'Run AI Analysis'}
            </button>
          )}
        </div>

        {analysing && (
          <div className="space-y-2">
            {['Reviewing incident evidence...', 'Mapping ICAM causal tree...', 'Identifying system defences...', 'Generating recommendations...'].map((m, i) => (
              <div key={i} className="flex items-center gap-3 text-sm p-3 rounded-lg animate-pulse" style={{ background: 'var(--bg)' }}>
                <Brain size={15} style={{ color: 'var(--purple)' }} />
                <span style={{ color: 'var(--text-secondary)' }}>{m}</span>
              </div>
            ))}
          </div>
        )}

        {inv.icam_analysis && !analysing && (
          <div>
            <button onClick={() => setAiOpen(o => !o)} className="w-full flex items-center justify-between p-3 rounded-lg text-sm"
              style={{ background: 'var(--purple-light)', color: 'var(--purple)', border: 'none', cursor: 'pointer' }}>
              <span className="font-semibold flex items-center gap-2"><Brain size={15} /> AI Full Analysis</span>
              {aiOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </button>
            {aiOpen && (
              <div className="mt-2 p-4 rounded-xl text-sm whitespace-pre-wrap" style={{ background: 'var(--purple-light)', color: 'var(--text)', lineHeight: 1.7 }}>
                {inv.icam_analysis}
                <p className="mt-3 text-xs" style={{ color: 'var(--purple)' }}>AI-assisted analysis — requires human review before finalising</p>
              </div>
            )}
          </div>
        )}

        {[
          { field: 'immediate_causes', label: 'Immediate Causes', hint: 'Actions or conditions that directly caused the incident' },
          { field: 'contributing_factors', label: 'Contributing Factors', hint: 'System failures and workplace factors that enabled the causes' },
          { field: 'root_causes', label: 'Root Causes / Failed Defences', hint: 'Organisational and management system deficiencies' },
          { field: 'recommendations', label: 'Recommendations', hint: 'Prioritised corrective actions to prevent recurrence' },
        ].map(({ field, label, hint }) => (
          <div key={field} className="space-y-1">
            <div className="flex items-center justify-between">
              <p className="text-sm font-semibold" style={{ color: 'var(--text)' }}>{label}</p>
              {!isClosed && editing !== field && (
                <button onClick={() => startEdit(field, inv[field])} className="text-xs" style={{ color: 'var(--accent)', background: 'none', border: 'none', cursor: 'pointer' }}>Edit</button>
              )}
            </div>
            {editing === field ? (
              <div className="space-y-2">
                <textarea rows={5} value={editText} onChange={e => setEditText(e.target.value)} placeholder={hint} autoFocus />
                <div className="flex gap-2">
                  <button onClick={saveField} disabled={savingEdit} className="btn btn-primary text-xs py-1.5">{savingEdit ? 'Saving...' : 'Save'}</button>
                  <button onClick={() => setEditing(null)} className="btn btn-secondary text-xs py-1.5">Cancel</button>
                </div>
              </div>
            ) : inv[field] ? (
              <p className="text-sm whitespace-pre-wrap p-3 rounded-lg" style={{ background: 'var(--bg)', color: 'var(--text)', lineHeight: 1.7, border: '1px solid var(--border)' }}>
                {inv[field]}
              </p>
            ) : (
              <p className="text-sm p-3 rounded-lg" style={{ background: 'var(--bg)', color: 'var(--text-muted)', border: '1px dashed var(--border)' }}>
                {hint} — {isClosed ? 'Not recorded' : 'click Edit to add'}
              </p>
            )}
          </div>
        ))}
      </div>

      {/* Communication to Workers */}
      <div className="card space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <MessageSquare size={18} style={{ color: inv.worker_comm_method ? 'var(--green)' : 'var(--orange)' }} />
            <h2 className="font-semibold" style={{ color: 'var(--text)' }}>Communication to Workers</h2>
            {!inv.worker_comm_method && !isClosed && (
              <span className="text-xs px-2 py-0.5 rounded-full font-semibold" style={{ background: '#FEF3C720', color: 'var(--orange)', border: '1px solid var(--orange)' }}>Required before closing</span>
            )}
          </div>
          {inv.worker_comm_method && !isClosed && !editingComm && (
            <button onClick={() => setEditingComm(true)} className="text-xs" style={{ color: 'var(--accent)', background: 'none', border: 'none', cursor: 'pointer' }}>Edit</button>
          )}
        </div>

        {!editingComm && inv.worker_comm_method ? (
          <div className="grid grid-cols-2 gap-3">
            <div className="p-3 rounded-lg" style={{ background: 'var(--bg)', border: '1px solid var(--border)' }}>
              <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Method</p>
              <p className="text-sm font-medium mt-0.5" style={{ color: 'var(--text)' }}>{commMethodLabel}</p>
            </div>
            {inv.worker_comm_date && (
              <div className="p-3 rounded-lg" style={{ background: 'var(--bg)', border: '1px solid var(--border)' }}>
                <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Date communicated</p>
                <p className="text-sm font-medium mt-0.5" style={{ color: 'var(--text)' }}>{new Date(inv.worker_comm_date).toLocaleDateString('en-AU')}</p>
              </div>
            )}
            {inv.worker_comm_by && (
              <div className="p-3 rounded-lg" style={{ background: 'var(--bg)', border: '1px solid var(--border)' }}>
                <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Communicated by</p>
                <p className="text-sm font-medium mt-0.5" style={{ color: 'var(--text)' }}>{inv.worker_comm_by}</p>
              </div>
            )}
            {inv.worker_comm_summary && (
              <div className="p-3 rounded-lg col-span-2" style={{ background: 'var(--bg)', border: '1px solid var(--border)' }}>
                <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Summary</p>
                <p className="text-sm whitespace-pre-wrap mt-0.5" style={{ color: 'var(--text)', lineHeight: 1.6 }}>{inv.worker_comm_summary}</p>
              </div>
            )}
          </div>
        ) : editingComm || (!isClosed && !inv.worker_comm_method) ? (
          <div className="space-y-3">
            <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
              Record how investigation findings were communicated to affected workers (WHS Regulation — post-investigation requirement).
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold mb-1 block" style={{ color: 'var(--text-secondary)' }}>Method *</label>
                <select value={commForm.method} onChange={e => setCommForm(f => ({ ...f, method: e.target.value }))}>
                  <option value="">Select method…</option>
                  {COMM_METHODS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold mb-1 block" style={{ color: 'var(--text-secondary)' }}>Date communicated</label>
                <input type="date" value={commForm.date} onChange={e => setCommForm(f => ({ ...f, date: e.target.value }))} />
              </div>
              <div>
                <label className="text-xs font-semibold mb-1 block" style={{ color: 'var(--text-secondary)' }}>Communicated by</label>
                <input
                  placeholder={user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() || 'Name…' : 'Name…'}
                  value={commForm.by}
                  onChange={e => setCommForm(f => ({ ...f, by: e.target.value }))}
                />
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold mb-1 block" style={{ color: 'var(--text-secondary)' }}>Summary of what was communicated</label>
              <textarea rows={3} placeholder="What findings, controls, and actions were communicated to workers?" value={commForm.summary} onChange={e => setCommForm(f => ({ ...f, summary: e.target.value }))} />
            </div>
            <div className="flex gap-2">
              <button onClick={saveComm} disabled={savingComm || !commForm.method} className="btn btn-primary text-sm">
                {savingComm ? 'Saving...' : 'Save'}
              </button>
              {editingComm && (
                <button onClick={() => setEditingComm(false)} className="btn btn-secondary text-sm">Cancel</button>
              )}
            </div>
          </div>
        ) : (
          <p className="text-sm" style={{ color: 'var(--text-muted)' }}>Communication recorded.</p>
        )}
      </div>

      {/* Evidence log */}
      <div className="card space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="font-semibold" style={{ color: 'var(--text)' }}>Evidence ({inv.evidence?.length || 0})</h2>
          {!isClosed && (
            <button onClick={() => setShowEvidence(!showEvidence)} className="btn btn-secondary text-sm flex items-center gap-1.5">
              <Plus size={14} /> Add Evidence
            </button>
          )}
        </div>

        {showEvidence && (
          <div className="space-y-3 p-4 rounded-xl" style={{ background: 'var(--bg)', border: '1px solid var(--border)' }}>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold mb-1 block" style={{ color: 'var(--text-secondary)' }}>Description *</label>
                <input placeholder="What is this evidence?" value={evForm.item_description} onChange={e => setEvForm(f => ({ ...f, item_description: e.target.value }))} />
              </div>
              <div>
                <label className="text-xs font-semibold mb-1 block" style={{ color: 'var(--text-secondary)' }}>Type</label>
                <select value={evForm.evidence_type} onChange={e => setEvForm(f => ({ ...f, evidence_type: e.target.value }))}>
                  {EVIDENCE_TYPES.map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold mb-1 block" style={{ color: 'var(--text-secondary)' }}>Location</label>
                <input placeholder="Where collected or stored" value={evForm.collection_location} onChange={e => setEvForm(f => ({ ...f, collection_location: e.target.value }))} />
              </div>
              <div>
                <label className="text-xs font-semibold mb-1 block" style={{ color: 'var(--text-secondary)' }}>Relevance</label>
                <input placeholder="Why is this relevant?" value={evForm.relevance} onChange={e => setEvForm(f => ({ ...f, relevance: e.target.value }))} />
              </div>
            </div>
            <div className="flex gap-2">
              <button onClick={addEvidence} disabled={savingEv || !evForm.item_description.trim()} className="btn btn-primary text-sm">
                {savingEv ? 'Saving...' : 'Add'}
              </button>
              <button onClick={() => setShowEvidence(false)} className="btn btn-secondary text-sm">Cancel</button>
            </div>
          </div>
        )}

        {inv.evidence?.length > 0 ? (
          <div className="space-y-2">
            {inv.evidence.map((ev: any, i: number) => (
              <div key={ev.id} className="flex items-start gap-3 p-3 rounded-lg" style={{ background: 'var(--bg)', border: '1px solid var(--border)' }}>
                <span className="text-xs font-mono mt-0.5" style={{ color: 'var(--text-muted)', flexShrink: 0 }}>{i + 1}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium" style={{ color: 'var(--text)' }}>{ev.item_description}</p>
                  <div className="flex items-center gap-3 mt-1 flex-wrap">
                    <span className="text-xs px-1.5 py-0.5 rounded" style={{ background: 'var(--accent-light)', color: 'var(--accent)' }}>{ev.evidence_type}</span>
                    {ev.collection_location && <span className="text-xs" style={{ color: 'var(--text-muted)' }}>📍 {ev.collection_location}</span>}
                    {ev.relevance && <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>{ev.relevance}</span>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-center py-4" style={{ color: 'var(--text-muted)' }}>No evidence items logged</p>
        )}
      </div>

      {/* Investigation team */}
      <div className="card space-y-4">
        <h2 className="font-semibold" style={{ color: 'var(--text)' }}>Investigation Team ({inv.team?.length || 0})</h2>
        {inv.team?.length > 0 ? (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {inv.team.map((m: any) => (
              <div key={m.id} className="p-3 rounded-lg text-sm" style={{ background: 'var(--bg)', border: '1px solid var(--border)' }}>
                <p className="font-medium" style={{ color: 'var(--text)' }}>{m.user_name || m.external_name}</p>
                <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>{m.role || m.job_title || '—'}</p>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm" style={{ color: 'var(--text-muted)' }}>No team members added</p>
        )}
      </div>

    </div>
  )
}
